/**
 * Lectura y escritura del texto del Tablero (descripción, criterios, comentarios).
 *
 * Vive aquí y no dentro de un componente porque lo usan dos: el que pinta el texto ya guardado
 * (`app-worklog-rich-text`) y el que lo captura (`app-worklog-editor`). Con una copia en cada uno,
 * el día que cambie la marca uno de los dos deja de entender lo que escribe el otro.
 *
 * Hay dos lecturas del mismo texto y no se pisan:
 *
 * - `parseRichText` — el troceo plano en prosa e imágenes. Lo usa el EDITOR, que solo necesita
 *   saber dónde va cada figura mientras se escribe.
 * - `parseRichBlocks` — el árbol de bloques y marcas en línea. Lo usa el LECTOR.
 *
 * **Asimetría deliberada:** el lector interpreta más de lo que el editor sabe escribir. El editor
 * serializa texto plano más `![alt](url)`, nunca `**` ni listas. Es correcto: el marcado no lo
 * teclea nadie en el panel, entra por el servidor MCP (`worklog_registrar`, `worklog_comentar`),
 * que es de donde vienen los intakes que se leían con los asteriscos a la vista.
 */

/** Un trozo del texto: prosa, o una imagen que alguien pegó dentro de ella. */
export interface RichSegment {
  kind: 'text' | 'image'
  /** Texto del tramo, o URL (o token diferido) de la imagen. */
  value: string
  alt?: string
}

//* Imagen en markdown: ![alt](url). Es la única marca que se interpreta, y a propósito — ver el
//* docblock de `app-worklog-rich-text` para por qué no hay librería de markdown.
const IMAGE_MD = /!\[([^\]]*)\]\(([^)\s]+)\)/g

/** Nombre de respaldo de una imagen sin nombre. Uno solo, para que los dos lados coincidan. */
export const IMAGE_FALLBACK_ALT = 'imagen pegada'

/**
 * Trocea el texto en prosa e imágenes conservando el orden. Se recorre con `exec` en vez de
 * `split` porque hacen falta las dos capturas (alt y url) Y la posición, para no perder el texto
 * que va entre dos imágenes.
 */
export function parseRichText(text: string): RichSegment[] {
  if (!text) return []
  const out: RichSegment[] = []
  let cursor = 0
  //* `lastIndex` se reinicia a mano: el regex es una constante del módulo con flag `g`, así que
  //* conserva estado entre llamadas y la segunda pasada empezaría a media cadena.
  IMAGE_MD.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = IMAGE_MD.exec(text)) !== null) {
    if (match.index > cursor) out.push({ kind: 'text', value: text.slice(cursor, match.index) })
    out.push({ kind: 'image', value: match[2], alt: match[1] || IMAGE_FALLBACK_ALT })
    cursor = match.index + match[0].length
  }
  if (cursor < text.length) out.push({ kind: 'text', value: text.slice(cursor) })
  return out
}

/** La marca de imagen tal como se guarda. Un solo lugar la escribe. */
export function imageMark(alt: string | null | undefined, url: string): string {
  return `![${alt || IMAGE_FALLBACK_ALT}](${url})`
}


/* ════════════════════════ El árbol de bloques ════════════════════════

   Por qué esto y no una librería de markdown.

   `app-worklog-rich-text` documentó la postura del repo: nada de HTML almacenado pintado con
   `innerHTML`, porque un fallo del sanitizador se vuelve XSS con persistencia, y el panel no tiene
   CSP en ninguna capa. Ese mismo docblock dejaba la puerta abierta — «si algún día se piden
   negritas y listas, esa sí es la conversación de traer un renderer de verdad».

   Esta es esa conversación, y la respuesta no es traer el renderer: es interpretar SIN producir
   HTML. Estas funciones devuelven DATOS, y la plantilla los pinta con elementos reales y con el
   texto saliendo por interpolación. La diferencia importa: con `marked` + un sanitizador, la
   seguridad depende de que el sanitizador no falle nunca; así, no hay nada que sanear porque nunca
   se construye HTML. Es una garantía más fuerte que la anterior, no una más débil.
   ════════════════════════════════════════════════════════════════════ */

/** Una marca dentro de una línea. `text` es el caso común y no lleva adorno. */
export interface InlineSpan {
  kind: 'text' | 'strong' | 'em' | 'code' | 'link' | 'image'
  /** El texto de la marca, o la URL cuando es `image`. */
  value: string
  /** Solo en `link`. */
  href?: string
  /** Solo en `image`. */
  alt?: string
}

/** Un bloque del texto. `items` solo lo llevan las listas. */
export interface RichBlock {
  kind: 'p' | 'h' | 'ul' | 'ol' | 'code' | 'hr' | 'image'
  /** Prosa ya troceada (p, h) o el contenido crudo (code). */
  spans?: InlineSpan[]
  /** Cada punto de una lista, ya troceado. */
  items?: InlineSpan[][]
  /** Nivel del encabezado, 1 a 3. */
  level?: number
  /** URL de la imagen y su texto alternativo. */
  value?: string
  alt?: string
}

const IMAGE_LINE = /^!\[([^\]]*)\]\(([^)\s]+)\)$/
const HEADING = /^(#{1,3})\s+(.*)$/
const BULLET = /^\s*[-*]\s+(.*)$/
const ORDERED = /^\s*\d+[.)]\s+(.*)$/
const RULE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/
const FENCE = /^\s*```/

//! El código va PRIMERO. Si no, `` `**x**` `` se leería como negrita dentro de un código, y lo que
//! alguien escribió para mostrar literalmente saldría interpretado — justo al revés de lo que pidió.
const INLINE = [
  { kind: 'code' as const, re: /`([^`]+)`/ },
  //! La imagen va ANTES que la liga: `![alt](url)` contiene un `[alt](url)`, así que con el orden
  //! invertido una captura pegada se pintaría como un enlace de texto. Es la marca que ya existía
  //! antes de este parser y la que no puede romperse.
  { kind: 'image' as const, re: /!\[([^\]]*)\]\(([^)\s]+)\)/ },
  { kind: 'link' as const, re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { kind: 'strong' as const, re: /\*\*([^*]+)\*\*/ },
  { kind: 'em' as const, re: /(?:^|[^*])\*([^*\n]+)\*/ },
]

/**
 * Trocea una línea en sus marcas. Recursivo sobre lo que queda a la derecha, para no depender del
 * estado de un regex global.
 *
 * Lo que no case con ninguna marca sale como `text` y la plantilla lo interpola: por eso un
 * `<script>` escrito en una descripción se ve como texto y no como una etiqueta.
 */
export function parseInline(line: string): InlineSpan[] {
  if (!line) return []

  let best: { kind: InlineSpan['kind']; at: number; len: number; value: string; href?: string; alt?: string } | null = null
  for (const marca of INLINE) {
    const m = marca.re.exec(line)
    if (!m) continue
    //* La cursiva mira un carácter antes para no morder el `**` de la negrita; ese carácter no es
    //* parte de la marca, así que el inicio real se corrige aquí.
    const offset = marca.kind === 'em' && !m[0].startsWith('*') ? 1 : 0
    const at = m.index + offset
    if (best && at >= best.at) continue
    best =
      marca.kind === 'image'
        ? { kind: marca.kind, at, len: m[0].length - offset, value: m[2], alt: m[1] || IMAGE_FALLBACK_ALT }
        : { kind: marca.kind, at, len: m[0].length - offset, value: m[1], href: marca.kind === 'link' ? m[2] : undefined }
  }

  if (!best) return [{ kind: 'text', value: line }]

  const out: InlineSpan[] = []
  if (best.at > 0) out.push({ kind: 'text', value: line.slice(0, best.at) })
  out.push({ kind: best.kind, value: best.value, href: best.href, alt: best.alt })
  //* El contenido de un código NO se vuelve a trocear: dentro de un código todo es literal.
  const resto = line.slice(best.at + best.len)
  return out.concat(resto ? parseInline(resto) : [])
}

/**
 * Convierte el texto guardado en bloques para pintar.
 *
 * Los saltos de línea sueltos dentro de un párrafo se conservan (la plantilla usa `pre-wrap`):
 * en un intake la gente separa ideas con un salto y perderlos cambiaría lo que quiso decir.
 */
export function parseRichBlocks(text: string): RichBlock[] {
  if (!text) return []
  //! Los saltos se normalizan ANTES de partir. Un `<textarea>` manda `\r\n` por especificación, y
  //! `\r` es terminador de línea en JavaScript: `.` no lo casa. Con el `\r` colgando al final de
  //! cada línea, los patrones que acaban en `(.*)$` —encabezado, viñeta, numerada— dejaban de
  //! reconocerse, mientras que los que acaban en `\s*$` sí. El resultado era que las listas y los
  //! títulos salían como párrafos y el separador no: incoherente y muy difícil de ver leyendo.
  const lineas = text.replace(/\r\n?/g, '\n').split('\n')
  const out: RichBlock[] = []
  let parrafo: string[] = []

  const cerrarParrafo = () => {
    if (!parrafo.length) return
    const cuerpo = parrafo.join('\n').trim()
    if (cuerpo) out.push({ kind: 'p', spans: parseInline(cuerpo) })
    parrafo = []
  }

  for (let i = 0; i < lineas.length; i++) {
    const linea = lineas[i]

    if (FENCE.test(linea)) {
      cerrarParrafo()
      const cuerpo: string[] = []
      i++
      while (i < lineas.length && !FENCE.test(lineas[i])) cuerpo.push(lineas[i++])
      //* Sin trocear: dentro de un bloque de código no hay marcas, hay código.
      out.push({ kind: 'code', spans: [{ kind: 'text', value: cuerpo.join('\n') }] })
      continue
    }

    const imagen = IMAGE_LINE.exec(linea.trim())
    if (imagen) {
      cerrarParrafo()
      out.push({ kind: 'image', value: imagen[2], alt: imagen[1] || IMAGE_FALLBACK_ALT })
      continue
    }

    if (RULE.test(linea)) {
      cerrarParrafo()
      out.push({ kind: 'hr' })
      continue
    }

    const titulo = HEADING.exec(linea)
    if (titulo) {
      cerrarParrafo()
      out.push({ kind: 'h', level: titulo[1].length, spans: parseInline(titulo[2]) })
      continue
    }

    const vinneta = BULLET.exec(linea)
    //* Ternario y no `!vinneta &&`: eso devolvía `false | RegExpExecArray` y el tipo no se estrecha.
    const numerada = vinneta ? null : ORDERED.exec(linea)
    if (vinneta || numerada) {
      cerrarParrafo()
      const kind = vinneta ? 'ul' : 'ol'
      const ultimo = out[out.length - 1]
      const punto = parseInline((vinneta ? vinneta[1] : numerada![1]).trim())
      //* Se agrupa con la lista anterior si es del mismo tipo: si no, cada punto sería su propia
      //* lista y las viñetas saldrían separadas por aire.
      if (ultimo && ultimo.kind === kind) ultimo.items!.push(punto)
      else out.push({ kind, items: [punto] })
      continue
    }

    if (!linea.trim()) {
      cerrarParrafo()
      continue
    }
    parrafo.push(linea)
  }

  cerrarParrafo()
  return out
}
